// frontend/src/components/playground/PlaygroundContainer.jsx
/**
 * Aura Playground - Monopoly Deal-style card game interface
 * Users drag Action Cards onto Portfolio Card to see simulations
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import {
  SparklesIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import StrategyCard from './StrategyCard';
import PlaygroundGuide from './PlaygroundGuide';
import MakeItRealButton from '../safety/MakeItRealButton.jsx';
import AuraPlaygroundHUD from './AuraPlaygroundHUD.jsx';

const PlaygroundContainer = ({ user, onClose }) => {
  const [sessionId, setSessionId] = useState(null);
  const [cards, setCards] = useState([]);
  const [auraCredits, setAuraCredits] = useState(5000);
  const [movesRemaining, setMovesRemaining] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Game state
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [draggedCard, setDraggedCard] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [visionProgress, setVisionProgress] = useState({});
  
  // Vision progress bars (from existing VisionBoard)
  const [visions, setVisions] = useState([]);

  useEffect(() => {
    if (user) {
      loadDailyCards();
      loadVisions();
      loadPortfolio();
    }
  }, [user]);

  const loadDailyCards = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/game/playground/draw-cards', {
        force_regenerate: false
      });
      
      if (response.data.success) {
        setSessionId(response.data.session_id);
        setCards(response.data.cards || []);
        setAuraCredits(response.data.aura_credits || 5000);
        setMovesRemaining(response.data.moves_remaining || 3);
      }
    } catch (err) {
      console.error('Error loading cards:', err);
      setError('Failed to load cards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadVisions = async () => {
    try {
      const response = await axios.get('/api/visions');
      if (response.data.success) {
        const activeVisions = response.data.visions.filter(v => v.status === 'active');
        setVisions(activeVisions);
        
        // Initialize vision progress
        const progress = {};
        activeVisions.forEach(vision => {
          const progressPct = (vision.current_amount / vision.target_amount) * 100;
          progress[vision.vision_id] = progressPct;
        });
        setVisionProgress(progress);
      }
    } catch (err) {
      console.error('Error loading visions:', err);
    }
  };

  const loadPortfolio = async () => {
    try {
      const response = await axios.get('/api/portfolio/summary');
      if (response.data.success) {
        setPortfolioValue(response.data.total_portfolio_value || 0);
      }
    } catch (err) {
      console.error('Error loading portfolio:', err);
    }
  };

  const handleCardDragStart = (card) => {
    setDraggedCard(card);
  };

  const handleCardDragEnd = async (card, event) => {
    setDraggedCard(null);
    
    // Check if dropped on portfolio card area
    const portfolioCard = document.getElementById('portfolio-card');
    if (!portfolioCard) return;
    
    const rect = portfolioCard.getBoundingClientRect();
    
    // Get position from event (Framer Motion provides point.x and point.y)
    const x = event?.clientX || event?.point?.x || (rect.left + rect.width / 2);
    const y = event?.clientY || event?.point?.y || (rect.top + rect.height / 2);
    
    // Check if card is over portfolio card (with generous tolerance for easier dropping)
    const tolerance = 100; // pixels - generous for easier dropping
    const isOverPortfolio = (
      x >= rect.left - tolerance && 
      x <= rect.right + tolerance && 
      y >= rect.top - tolerance && 
      y <= rect.bottom + tolerance
    );
    
    if (isOverPortfolio) {
      // Card dropped on portfolio - run simulation
      await playCard(card);
    } else {
      // Card dropped outside - show hint
      setError('💡 Tip: Drag the card onto the Portfolio card to play it, or click the "Play Card" button!');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Alternative: Click to play (fallback if drag doesn't work)
  const handleCardClick = async (card) => {
    if (!card.played && movesRemaining > 0) {
      await playCard(card);
    }
  };

  const playCard = async (card) => {
    // Prevent double-clicking or playing already played cards
    if (card.played) {
      setError('This card has already been played.');
      return;
    }

    if (movesRemaining <= 0) {
      setError('No moves remaining for today. Come back tomorrow!');
      return;
    }

    // Prevent multiple simultaneous requests
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/game/playground/simulate', {
        session_id: sessionId,
        card_id: card.card_id,
        card_type: card.card_type,
        allocation_percentage: 20.0
      });

      // Check if response indicates card already played
      if (!response.data.success && response.data.error === "Card already played") {
        setError(response.data.message || 'This card has already been played.');
        // Mark card as played in UI
        setCards(prevCards => 
          prevCards.map(c => 
            c.card_id === card.card_id 
              ? { ...c, played: true }
              : c
          )
        );
        return;
      }

      if (response.data.success) {
        const result = response.data.simulation_result;
        console.log('Simulation result received:', result);
        
        // Ensure we have the full result structure
        const fullResult = {
          ...result,
          portfolio_delta: result.portfolio_delta || {},
          vision_impacts: response.data.vision_impacts || result.vision_impacts || [],
          before_state: result.before_state || {},
          after_state: result.after_state || {}
        };
        
        setSimulationResult(fullResult);
        
        // Update vision progress with animation
        if (response.data.vision_impacts && response.data.vision_impacts.length > 0) {
          const newProgress = { ...visionProgress };
          response.data.vision_impacts.forEach(impact => {
            if (impact.vision_id && impact.new_progress !== undefined) {
              newProgress[impact.vision_id] = impact.new_progress;
            }
          });
          setVisionProgress(newProgress);
        }
        
        // Mark card as played immediately to prevent re-playing
        setCards(prevCards => 
          prevCards.map(c => 
            c.card_id === card.card_id 
              ? { ...c, played: true, played_at: new Date().toISOString() }
              : c
          )
        );
        
        // Update moves remaining
        setMovesRemaining(prev => Math.max(0, prev - 1));
        
        // Scroll to simulation result
        setTimeout(() => {
          const resultElement = document.querySelector('[data-simulation-result]');
          if (resultElement) {
            resultElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        setError(response.data.message || 'Simulation failed. Please try again.');
      }
    } catch (err) {
      console.error('Error playing card:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to play card. Please try again.';
      
      // Handle specific errors
      if (errorMessage.includes('already played') || errorMessage.includes('Card already played')) {
        // Mark card as played in UI even if backend says it's already played
        setCards(prevCards => 
          prevCards.map(c => 
            c.card_id === card.card_id 
              ? { ...c, played: true }
              : c
          )
        );
        setError('This card has already been played. Please select another card.');
      } else {
        setError(`Simulation failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCommitStrategy = async () => {
    if (!simulationResult) return;

    setLoading(true);
    try {
      // This would call the commit endpoint
      // For now, just show success message
      alert('Strategy committed! Aura will help you execute this in your brokerage account.');
      setShowCommitModal(false);
      setSimulationResult(null);
    } catch (err) {
      console.error('Error committing strategy:', err);
      setError('Failed to commit strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !cards.length) {
    return (
      <div className="fixed inset-0 bg-cosmic-dark/90 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="text-white text-xl">Loading your cards...</div>
      </div>
    );
  }

  return (
    <AuraPlaygroundHUD
      isSimulationMode={true}
      cards={cards}
      onCardSelect={(card) => handleCardClick(card)}
    >
    <div className="fixed inset-0 bg-cosmic-dark/95 backdrop-blur-md z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-cosmic-dark/80 backdrop-blur-lg border-b border-aurora-purple/20 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <SparklesIcon className="w-8 h-8 text-aurora-purple" />
            <h1 className="text-2xl font-bold text-white">Aura Playground</h1>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-white">
              <CurrencyDollarIcon className="w-5 h-5 text-aurora-gold" />
              <span className="font-semibold">{auraCredits.toLocaleString()} Credits</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <ArrowPathIcon className="w-5 h-5 text-aurora-blue" />
              <span className="font-semibold">{movesRemaining} Moves Left</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-aurora-purple/20 hover:bg-aurora-purple/30 text-white transition"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200"
          >
            {error}
          </motion.div>
        )}

        {/* Game Board */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" style={{ minHeight: '500px' }}>
          {/* Portfolio Card (Drop Zone) */}
          <motion.div
            id="portfolio-card"
            className="lg:col-span-2 bg-glass-card backdrop-blur-xl rounded-2xl p-6 border border-aurora-purple/30 relative"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedCard) {
                playCard(draggedCard);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <ChartBarIcon className="w-6 h-6 text-aurora-blue" />
              <h2 className="text-xl font-bold text-white">Your Portfolio</h2>
            </div>
            
            <div className="mb-6">
              <div className="text-3xl font-bold text-white mb-2">
                ${portfolioValue.toLocaleString()}
              </div>
              <div className="text-sm text-gray-400">Total Portfolio Value</div>
            </div>

            {/* Vision Progress Bars */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-3">Vision Progress</h3>
              {visions.map(vision => {
                const progress = visionProgress[vision.vision_id] || 0;
                return (
                  <div key={vision.vision_id} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white">{vision.title}</span>
                      <span className="text-gray-400">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-aurora-purple to-aurora-blue"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Drop Zone Indicator */}
            <div className="mt-6 p-4 border-2 border-dashed border-aurora-purple/30 rounded-lg text-center">
              {draggedCard ? (
                <motion.div
                  className="text-aurora-purple"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <p className="text-lg font-semibold">Drop card here to simulate</p>
                  <p className="text-sm text-gray-400 mt-1">Release to see the impact on your visions</p>
                </motion.div>
              ) : (
                <div className="text-gray-400">
                  <p className="text-sm">💡 Drag a card here or click a card to play</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Card Hand */}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-white mb-4">Your Cards</h3>
            <div className="flex flex-col gap-4 max-h-[600px] overflow-y-auto pr-2">
              {cards.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <p>No cards available. Draw new cards to play!</p>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {cards.map((card, index) => (
                    <motion.div
                      key={card.card_id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, scale: 0.9 }}
                      transition={{ 
                        delay: index * 0.05, 
                        type: "spring", 
                        stiffness: 300,
                        damping: 25
                      }}
                      className="w-full flex-shrink-0"
                      style={{ position: 'relative' }}
                    >
                      <StrategyCard
                        card={card}
                        onDragStart={() => handleCardDragStart(card)}
                        onDragEnd={(e) => handleCardDragEnd(card, e)}
                        onClick={() => handleCardClick(card)}
                        disabled={card.played || movesRemaining <= 0 || loading}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* Simulation Result */}
        {simulationResult && (
          <motion.div
            data-simulation-result
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-glass-card backdrop-blur-xl rounded-2xl p-6 border border-aurora-green/30 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">✨ Simulation Result</h3>
              <button
                onClick={() => setSimulationResult(null)}
                className="text-gray-400 hover:text-white transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Portfolio Changes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-aurora-purple/10 rounded-lg p-4 border border-aurora-purple/20">
                <div className="text-sm text-gray-400 mb-1">Portfolio Value Change</div>
                <div className={`text-3xl font-bold ${
                  (simulationResult.portfolio_delta?.value_change || 0) >= 0 
                    ? 'text-aurora-green' 
                    : 'text-red-400'
                }`}>
                  {(simulationResult.portfolio_delta?.value_change || 0) >= 0 ? '+' : ''}
                  ${Math.abs(simulationResult.portfolio_delta?.value_change || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                {simulationResult.portfolio_delta?.value_change_pct && (
                  <div className="text-xs text-gray-400 mt-1">
                    ({simulationResult.portfolio_delta.value_change_pct > 0 ? '+' : ''}{simulationResult.portfolio_delta.value_change_pct.toFixed(2)}%)
                  </div>
                )}
              </div>
              <div className="bg-aurora-blue/10 rounded-lg p-4 border border-aurora-blue/20">
                <div className="text-sm text-gray-400 mb-1">Monthly Income Change</div>
                <div className={`text-3xl font-bold ${
                  (simulationResult.portfolio_delta?.income_change || 0) >= 0 
                    ? 'text-aurora-green' 
                    : 'text-red-400'
                }`}>
                  {(simulationResult.portfolio_delta?.income_change || 0) >= 0 ? '+' : ''}
                  ${Math.abs(simulationResult.portfolio_delta?.income_change || 0).toFixed(2)}/mo
                </div>
                {simulationResult.portfolio_delta?.income_change_pct && (
                  <div className="text-xs text-gray-400 mt-1">
                    ({simulationResult.portfolio_delta.income_change_pct > 0 ? '+' : ''}{simulationResult.portfolio_delta.income_change_pct.toFixed(2)}%)
                  </div>
                )}
              </div>
            </div>

            {/* Vision Impacts */}
            {simulationResult.vision_impacts && simulationResult.vision_impacts.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-3">Vision Impacts</h4>
                <div className="space-y-3">
                  {simulationResult.vision_impacts.map((impact, idx) => (
                    <div key={idx} className="bg-aurora-green/10 rounded-lg p-3 border border-aurora-green/20">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">{impact.title || impact.vision_id}</span>
                        <span className={`font-bold ${
                          (impact.delta_years || 0) < 0 ? 'text-aurora-green' : 
                          (impact.delta_years || 0) > 0 ? 'text-red-400' : 'text-gray-400'
                        }`}>
                          {(impact.delta_years || 0) < 0 ? '✅' : (impact.delta_years || 0) > 0 ? '⏳' : '➡️'} 
                          {' '}
                          {Math.abs(impact.delta_years || 0) > 0 
                            ? `${Math.abs(impact.delta_years).toFixed(1)} years ${(impact.delta_years || 0) < 0 ? 'closer' : 'further'}`
                            : 'No change'
                          }
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Risk Score Change */}
            {simulationResult.portfolio_delta?.risk_change !== undefined && (
              <div className="mb-6 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-white">Risk Score Change</span>
                  <span className={`font-bold ${
                    (simulationResult.portfolio_delta.risk_change || 0) > 0 ? 'text-yellow-400' : 'text-aurora-green'
                  }`}>
                    {(simulationResult.portfolio_delta.risk_change || 0) > 0 ? '+' : ''}
                    {simulationResult.portfolio_delta.risk_change?.toFixed(1)}
                  </span>
                </div>
              </div>
            )}

            <MakeItRealButton
              onConfirm={() => setShowCommitModal(true)}
              simulationResult={simulationResult}
            />
          </motion.div>
        )}
      </div>

      {/* Commit Modal */}
      <AnimatePresence>
        {showCommitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCommitModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-glass-card backdrop-blur-xl rounded-2xl p-6 border border-aurora-purple/30 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Make it Real?</h3>
              <p className="text-gray-300 mb-6">
                This will help you execute this strategy in your real brokerage account.
                Aura will guide you through the steps to make these changes.
              </p>
              
              <div className="flex gap-4">
                <button
                  onClick={() => setShowCommitModal(false)}
                  className="flex-1 py-2 px-4 bg-gray-700 rounded-lg text-white hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCommitStrategy}
                  className="flex-1 py-2 px-4 bg-gradient-to-r from-aurora-purple to-aurora-blue rounded-lg text-white font-semibold hover:opacity-90 transition"
                >
                  Commit Strategy
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Playground Guide Character - Always Available */}
      <PlaygroundGuide
        user={user}
        gameState={{
          cards,
          movesRemaining,
          simulationResult,
          visionProgress,
          visions,
          portfolioValue
        }}
        onAction={(action) => {
          // Handle guide actions
          console.log('Guide action:', action);
        }}
        currentStep={
          simulationResult ? 'after_simulation' : 
          cards.length > 0 && movesRemaining > 0 ? 'ready_to_play' : 
          cards.length > 0 && movesRemaining <= 0 ? 'no_moves' :
          'loading'
        }
      />
    </div>
    </AuraPlaygroundHUD>
  );
};

export default PlaygroundContainer;
